
import Resources from '../../../helpers/resources';

// Helpers
import { actionCreator, getLocale } from '../../../helpers/utils';

// Other
import coursesActions, { FETCHING_COURSE_CONTENT } from './courses-actions';
import PortalCommunication from '../../../helpers/portal-communications';
import { actionTypeIds } from '../../../helpers/enums';
import { trackEvent, ANALYTICS_EVENTS } from '../../../helpers/analytics';

// List of action names
export const GET_TEACHER_CLASS = 'GET_TEACHER_CLASS';
export const GET_UPDATED_TEACHER_CLASS = 'GET_UPDATED_TEACHER_CLASS';
export const EDIT_CLASS_NAME = 'EDIT_CLASS_NAME';
export const CHANGE_PASSWORD = 'CHANGE_PASSWORD';

const getTeacherClasses = productId => async (dispatch) => {
    if (Resources.getTeacherClasses) {
        const getTeacherClassesResponse = await Resources.getTeacherClasses
            .get({ productId })
            .headers({ Locale: getLocale() })
            .send();

        if (getTeacherClassesResponse.valid) {
            const teacherAction = actionCreator(GET_TEACHER_CLASS, {
                data: getTeacherClassesResponse.data,
                productId: parseInt(productId, 10)
            });
            await dispatch(teacherAction);

            return true;
        }

        return getTeacherClassesResponse;
    }

    return false;
};

const editClassName = ({ classroomId, name }) => async (dispatch) => {
    if (Resources.editClassName) {
        // Prevent a .raw() chain method so that errors can be handled properly
        //      -> although the user will get a console error, it's a trade off we need
        const editClassResponse = await Resources.editClassName
            .put({ classroomId })
            .headers({ Locale: getLocale() })
            .withBody(name)
            // .raw()
            .send();

        if (editClassResponse.valid) {
            const teacherAction = actionCreator(EDIT_CLASS_NAME, {
                name
            });
            dispatch(teacherAction);
            trackEvent(ANALYTICS_EVENTS.classRenamed);

            return true;
        }

        // Else for other errors, such as duplicate name, etc
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'classes' });
        return editClassResponse;
    }

    return false;
};

const updateCurrentClassId = ({ productId, classId }) => async (dispatch) => {
    if (Resources.getTeacherClasses) {
        const getTeacherClassesResponse = await Resources.getTeacherClasses
            .get({ productId })
            .withParams({ classId })
            .headers({ Locale: getLocale() })
            .send();

        // Inform change in ClassId to portal
        PortalCommunication.informClassGroupSelection(classId);
        // Set the new data based on the class
        if (getTeacherClassesResponse.valid) {
            const data = getTeacherClassesResponse.data;
            const teacherAction = actionCreator(GET_UPDATED_TEACHER_CLASS, {
                data
            });
            await dispatch(teacherAction);

            const fetchingData = actionCreator(FETCHING_COURSE_CONTENT);
            await dispatch(fetchingData);

            return true;
        }
    }

    return false;
};

const changePassword = ({ userProfileId, newPassword }) => async (dispatch) => {
    if (Resources.changePassword) {
        // Prevent a .raw() chain method so that errors can be handled properly
        //      -> although the user will get a console error, it's a trade off we need
        const changePasswordResponse = await Resources.changePassword
            .put()
            .headers({ Locale: getLocale() })
            .withBody({
                userProfileId,
                newPassword
            })
            .send();

        if (changePasswordResponse.valid) {
            const teacherAction = actionCreator(CHANGE_PASSWORD, {
                userProfileId,
                newPassword
            });
            dispatch(teacherAction);
            trackEvent(ANALYTICS_EVENTS.passwordChanged);

            return true;
        }

        // Else for other errors, such as duplicate name, etc
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'passwords' });
        return changePasswordResponse;
    }
    return false;
};

// Add new students actions ---- start ----
// Get all students data while move/copy students through footer action
const getInitialStudentsForMoveCopy = ({ productId }) => async () => {
    if (Resources.getStudentMoveCopyData) {
        const addNewStudentResponse = await Resources.getStudentMoveCopyData
            .get({ productId: productId || 1 })
            .headers({ Locale: getLocale() })
            .send();

        if (addNewStudentResponse.valid) {
            return addNewStudentResponse.data;
        }

        return addNewStudentResponse.valid;
    }

    return false;
};

// Get all students data while move/copy students through ADD Students action
const getInitialAddStudent = ({ productId, currentClassId }) => async () => {
    if (Resources.getStudentManagementData) {
        const addNewStudentResponse = await Resources.getStudentManagementData
            .get({ productId: productId || 1 })
            .headers({ Locale: getLocale() })
            .withParams({ currentClassId })
            .send();

        if (addNewStudentResponse.valid) {
            return addNewStudentResponse.data;
        }
        return addNewStudentResponse.valid;
    }

    return false;
};

// Get student management data for a particular class
const getAddStudentClasses = ({ classId, productId }) => async () => {
    if (Resources.getAddStudentClasses) {
        const addNewStudentResponse = await Resources.getAddStudentClasses
            .get({ classId, productId })
            .headers({ Locale: getLocale() })
            .send();

        if (addNewStudentResponse.valid) {
            return addNewStudentResponse.data;
        }
        return addNewStudentResponse.valid;
    }
    return false;
};

// Get student management data for a particular class & group
const getAddStudentDetails = ({ productId, classId, groupId }) => async () => {
    if (Resources.getAddStudentDetails) {
        const addNewStudentResponse = await Resources.getAddStudentDetails
            .get({ productId, classId, groupId })
            .headers({ Locale: getLocale() })
            .send();

        if (addNewStudentResponse.valid) {
            return addNewStudentResponse.data;
        }
        return addNewStudentResponse.valid;
    }
    return false;
};

/**
 * This function will update a students relation to a particular class, the function to move/copy them is based on
 * the action(move/copy/remove) selected, as mentioned in the updateMoveStudentList method.
 *
 *      - Move will move the student from their current class (referred as sourceClassId) and to the new class and group
 *      - Copy will do the same thing as above, instead they will stay in their current class (sourceClassId)
 *      - Remove will remove the student from the particular class and group
 *
 * @param assignStudents
 */
const assignClassStudent = ({ assignStudents }) => async () => {
    // Filters out any copy or move without a groupId
    const cleanAssignStudents = assignStudents.filter(student =>
        ((student.actionType === 1 || student.actionType === 2) && Number.isInteger(student.destinationGroupId))
        || student.actionType === 3
    );
    if (Resources.assignClassStudent) {
        const assignClassStudentResponse = await Resources.assignClassStudent
            .patch()
            .headers({ Locale: getLocale() })
            .withBody({
                assignStudents: cleanAssignStudents
            })
            .raw()
            .send();

        if (assignClassStudentResponse.valid) {
            const movedCount = cleanAssignStudents.filter(student => student.actionType === actionTypeIds.move).length;
            const copiedCount = cleanAssignStudents.filter(student => student.actionType === actionTypeIds.copy).length;
            const removedCount = cleanAssignStudents.filter(student => student.actionType === actionTypeIds.remove).length;

            if (movedCount > 0) {
                trackEvent(ANALYTICS_EVENTS.studentsMoved, { count: movedCount });
            }
            if (copiedCount > 0) {
                trackEvent(ANALYTICS_EVENTS.studentsCopied, { count: copiedCount });
            }
            if (removedCount > 0) {
                trackEvent(ANALYTICS_EVENTS.studentsRemoved, { count: removedCount });
            }

            return assignClassStudentResponse.valid;
        }
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'students' });
        return false;
    }
    return false;
};

/**
 * This function will update a students relation to a particular class, in the table and
 * does not change the group in the footer modal
 *
 *      - Move the student to the selected group in the table
 *      - This is created using the new endpoint gateway
 *
 *
 * @param classId
 * @param groupId
 * @param studentProfileIds
 */
const assignGroup = ({
    classId,
    groupId,
    studentProfileIds
}) => async () => {
    if (Resources.assignGroup) {
        const assignStudentResponse = await Resources.assignGroup
            .patch({ classId, groupId })
            .headers({ Locale: getLocale() })
            .withBody(studentProfileIds)
            .raw()
            .send();

        if (assignStudentResponse.valid) {
            trackEvent(ANALYTICS_EVENTS.studentsGroupChanged, {
                count: studentProfileIds ? studentProfileIds.length : 0
            });
            return assignStudentResponse.data;
        }
        trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'students' });
        return false;
    }
    return false;
};
// Add new students actions ---- end ----

const exportStudents = () => async (dispatch, getState) => {
    const consoleReducer = getState().consoleReducer;
    const {
        productId,
        currentClassId
    } = consoleReducer;

    const authToken = Resources.env.authToken;
    const getExportStudents = Resources.exportStudents;
    const endpointValue = 'StudentManagerGatewayEndpoint';
    const service = getExportStudents.configuration.values[endpointValue];
    let url = getExportStudents.path;

    // replace the variables within the path of the key
    url = url.replace(`@${endpointValue}`, service);
    url = url.replace('{productId}', productId);
    url = url.replace('{currentClassId}', currentClassId);
    url = url.replace('{authToken}', authToken);
    trackEvent(ANALYTICS_EVENTS.studentsExported);
    window.location.href = url;
};

const downloadSignInCardsHandler = ({
    signInCardPageSizeType,
    productId,
    groupId,
    currentClassId,
    studentUserProfileIds
}) => async (dispatch) => {
    if (Resources.downloadStudentSignInCards) {
        let downloadBody = {};

        if (groupId === '') {
            downloadBody = {
                SignInCardPageSizeType: signInCardPageSizeType,
                ClassroomId: currentClassId,
                StudentUserProfileIds: studentUserProfileIds
            };
        } else {
            downloadBody = {
                SignInCardPageSizeType: signInCardPageSizeType,
                ClassroomId: currentClassId,
                GroupId: groupId,
                StudentUserProfileIds: studentUserProfileIds
            };
        }

        const downloadStudentSignInResponse = await Resources.downloadStudentSignInCards
            .post({ productId })
            .headers({ Locale: getLocale() })
            .withBody(downloadBody)
            .send();

        if (downloadStudentSignInResponse.valid) {
            const downloadKey = downloadStudentSignInResponse.data;
            const getDownloadCards = Resources.getDownloadStudentSignInCards;
            let url = getDownloadCards.path;

            const endpointValue = 'SignInCardGatewayEndpoint';
            const service = getDownloadCards.configuration.values[endpointValue];
            // replace the variables within the path of the key
            url = url.replace(`@${endpointValue}`, service);
            url = url.replace('{productId}', productId);
            url = url.replace('{downloadKey}', downloadKey);
            window.location.href = url;

            trackEvent(ANALYTICS_EVENTS.signInCardsDownloaded, {
                count: studentUserProfileIds ? studentUserProfileIds.length : 0
            });

            dispatch({
                type: 'SUCCESS_DOWNLOAD',
                payload: {
                    downloadSuccess: true
                }
            });
        }

        if (!downloadStudentSignInResponse.valid) {
            trackEvent(ANALYTICS_EVENTS.sectionError, { section: 'signin' });
        }

        return downloadStudentSignInResponse.valid;
    }

    return false;
};

export default {
    getTeacherClasses,
    editClassName,
    updateCurrentClassId,
    changePassword,
    getInitialAddStudent,
    getInitialStudentsForMoveCopy,
    getAddStudentClasses,
    getAddStudentDetails,
    assignClassStudent,
    assignGroup,
    downloadSignInCardsHandler,
    ...coursesActions,
    exportStudents
};



// WEBPACK FOOTER //
// ./containers/console/actions/console-actions.js